import { createSlice } from '@reduxjs/toolkit'
import { AI_SUBSCRIPTION_TYPES } from 'constants/ai'
import {
  PaddleSubscriptionBillings,
  PaddleSubscriptionResponse,
  PaddleSubscriptionScheduledCancel,
  PaddleSubscriptionStatus,
  PaddleSubscriptionType,
} from 'interface/paddle'
import { RootState } from 'store'

export type FetchSubscriptionDataPayload = { uid: string }

export type SubscriptionMessage = { role: 'user' | 'assistant'; content: string }

export type PaddleSubscriptionApiResponse = PaddleSubscriptionResponse & {
  message?: string
  error?: string
}

export type SetAiSubscriptionPayload = { uid: string }

// Define a type for the slice State
export type SubscriptionProps = {
  aiSubscriptionType: PaddleSubscriptionType | null
  nextBillingDate: string | null
  aiSubscriptionStatus: PaddleSubscriptionStatus | null
  aiSubscriptionId: string | null
  aiSubscriptionLoading: boolean
  aiSubscriptionLoaded: boolean
  aiSubscriptionError: string | null
  isAdFreeOnly: boolean | null
  billings: PaddleSubscriptionBillings | null
  scheduledCancel: PaddleSubscriptionScheduledCancel | null
}

const initialState: SubscriptionProps = Object.freeze({
  aiSubscriptionType: null,
  nextBillingDate: null,
  aiSubscriptionStatus: null,
  aiSubscriptionId: null,
  aiSubscriptionLoading: false,
  isAdFreeOnly: null,
  billings: null,
  scheduledCancel: null,
  aiSubscriptionLoaded: false,
  aiSubscriptionError: null,
})

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setAiSubscriptionLoading: (state, action: { payload: boolean }) => {
      const { payload } = action
      state.aiSubscriptionLoading = payload
      state.aiSubscriptionLoaded = false
    },
    fetchAiSubscription: (_state, _action: { payload: SetAiSubscriptionPayload }) => {
      // saga watcher, you should call this with the payload from the App
    },
    __setAiSubscription: (state, action: { payload: PaddleSubscriptionApiResponse }) => {
      state.aiSubscriptionType = action.payload.type as PaddleSubscriptionType
      state.nextBillingDate = action.payload.nextBillingDate
      state.aiSubscriptionStatus = action.payload.status
      state.isAdFreeOnly = action.payload.isAdFreeOnly
      state.aiSubscriptionId = action.payload.paddleSubscriptionId
      state.billings = action.payload.billings
      state.scheduledCancel = action.payload.scheduledCancel
      state.aiSubscriptionLoading = false
      state.aiSubscriptionLoaded = true
      state.aiSubscriptionError = null
    },
    setAiSubscriptionError: (state, action: { payload: string }) => {
      state.aiSubscriptionLoading = false
      state.aiSubscriptionError = action.payload
    },
  },
})

export const { setAiSubscriptionLoading, fetchAiSubscription, __setAiSubscription, setAiSubscriptionError } =
  subscriptionSlice.actions

export { subscriptionSlice }

export const selectStatSlice = (state: RootState) => state.subscription

export const selectAiSubscriptionType = (state: RootState) => selectStatSlice(state).aiSubscriptionType
export const selectNextBillingDate = (state: RootState) => selectStatSlice(state).nextBillingDate
export const selectAiSubscriptionStatus = (state: RootState) => selectStatSlice(state).aiSubscriptionStatus
export const selectAiSubscriptionId = (state: RootState) => selectStatSlice(state).aiSubscriptionId
export const selectAiSubscriptionLoading = (state: RootState) => selectStatSlice(state).aiSubscriptionLoading
export const selectAiSubscriptionLoaded = (state: RootState) => selectStatSlice(state).aiSubscriptionLoaded
export const selectAiSubscriptionError = (state: RootState) => selectStatSlice(state).aiSubscriptionError
export const selectAiSubscriptionIsAdFreeOnly = (state: RootState) => selectStatSlice(state).isAdFreeOnly
export const selectAiSubscriptionBillings = (state: RootState) => selectStatSlice(state).billings
export const selectAiSubscriptionScheduledCancel = (state: RootState) => selectStatSlice(state).scheduledCancel

// Check if the subscription is active  (core/root check)
export const selectIsAiSubscriptionActive = (state: RootState) => {
  return selectAiSubscriptionStatus(state) === 'active' || selectAiSubscriptionStatus(state) === 'trialing'
}

// Check if the subscription is expired
export const selectIsAiSubscriptionExpired = (state: RootState) => {
  return selectAiSubscriptionStatus(state) === 'past_due'
}

// Check if the subscription is active and not ad-free only
// This is used to check if the user has a subscription and it's not ad-free only
// This subscriber should be able to access the personalized AI experience and not just the ad-free experience (High paying customers)
export const selectIsAiSubscriptionActiveAndNotAdFreeOnly = (state: RootState) => {
  const isActive = selectIsAiSubscriptionActive(state)
  const isAdFreeOnly = selectAiSubscriptionIsAdFreeOnly(state)
  return isActive && !isAdFreeOnly
}

// Check if the subscription is canceled
export const selectIsAiSubscriptionCanceled = (state: RootState) => {
  return selectAiSubscriptionStatus(state) === 'canceled'
}

// Get the billing period
export const selectAiSubscriptionBillingPeriod = (state: RootState) => {
  const status = selectAiSubscriptionStatus(state)
  const type = selectAiSubscriptionType(state)
  if (status === 'canceled') return null
  return type === 'monthly' ? 'Monthly' : 'Yearly'
}

// Check if the subscription is in trial
export const selectIsAiSubscriptionInTrial = (state: RootState) => {
  const status = selectAiSubscriptionStatus(state)
  return status === AI_SUBSCRIPTION_TYPES.TRIAL
}

// Check the user has expired their trial
// This is used to check if the user has expired their trial and should be shown the trial expired modal
export const selectIsAiSubscriptionTrialExpired = (state: RootState) => {
  const status = selectAiSubscriptionStatus(state)
  const expired = selectIsAiSubscriptionExpired(state)

  return status === AI_SUBSCRIPTION_TYPES.TRIAL && expired
}
